/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import clsx from 'clsx';
import { cn } from '@/utils/cn';

/**
 * Typography component inspired by MUI but built with TailwindCSS.
 * - Written in TypeScript
 * - `variant` controls semantic styling
 * - `component` allows override of the rendered element
 * - `color` maps to Tailwind text color classes or accepts custom class
 */

type Variant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'caption'
  | 'overline'
  | 'button';

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: Variant;
  /**
   * Override rendered element: 'p', 'span', 'h1', etc.
   * If omitted, a good default is chosen per variant.
   */
  component?: keyof JSX.IntrinsicElements;
  /** Accepts Tailwind color classes like 'text-gray-700' OR one of the shortcuts. */
  color?: 'inherit' | 'primary' | 'secondary' | 'textPrimary' | 'textSecondary' | 'error' | string;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  gutterBottom?: boolean;
  noWrap?: boolean;
  className?: string;
}

const variantClassMap: Record<Variant, string> = {
  h1: 'text-4xl md:text-5xl leading-tight font-extrabold',
  h2: 'text-3xl md:text-4xl leading-tight font-bold',
  h3: 'text-2xl md:text-3xl leading-tight font-semibold',
  h4: 'text-xl md:text-2xl leading-snug font-semibold',
  h5: 'text-lg md:text-xl leading-snug font-medium',
  h6: 'text-base md:text-lg leading-snug font-medium',
  subtitle1: 'text-base leading-relaxed font-medium',
  subtitle2: 'text-sm leading-relaxed font-medium',
  body1: 'text-base leading-relaxed',
  body2: 'text-sm leading-relaxed',
  caption: 'text-xs leading-tight',
  overline: 'text-xs uppercase tracking-widest',
  button: 'text-sm font-medium uppercase tracking-wide',
};

const defaultComponentMap: Record<Variant, keyof JSX.IntrinsicElements> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  caption: 'span',
  overline: 'span',
  button: 'span',
};

function resolveColorClass(color?: TypographyProps['color']) {
  if (!color || color === 'textPrimary') return 'text-gray-900';
  if (color === 'textSecondary') return 'text-gray-600';
  if (color === 'primary') return 'text-blue-600';
  if (color === 'secondary') return 'text-indigo-600';
  if (color === 'error') return 'text-red-600';
  if (color === 'inherit') return '';
  // if user passed a class like 'text-gray-700' or a custom css string
  return color;
}

export const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant = 'body1',
      component,
      color = 'textPrimary',
      align = 'inherit',
      gutterBottom = false,
      noWrap = false,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    const Component = (component ?? defaultComponentMap[variant]) as any;

    const alignClass =
      align === 'inherit'
        ? ''
        : align === 'left'
          ? 'text-left'
          : align === 'center'
            ? 'text-center'
            : align === 'right'
              ? 'text-right'
              : 'text-justify';

    const wrapClass = noWrap ? 'truncate whitespace-nowrap overflow-hidden' : '';
    const gutterClass = gutterBottom ? 'mb-2' : '';

    const classes = cn(
      variantClassMap[variant],
      resolveColorClass(color),
      alignClass,
      wrapClass,
      gutterClass,
      className
    );

    return (
      <Component ref={ref} className={classes} {...rest}>
        {children}
      </Component>
    );
  }
);

Typography.displayName = 'Typography';

export default Typography;

/*
  ---- Usage examples ----

  import Typography from './Typography';

  function Demo() {
    return (
      <div className="space-y-4 p-6">
        <Typography variant="h1">Page title</Typography>
        <Typography variant="h2" color="primary">Section title</Typography>
        <Typography variant="subtitle1" color="textSecondary">A subtitle with lighter text</Typography>
        <Typography variant="body1">This is a normal paragraph of body text. Use body1 for main content.</Typography>
        <Typography variant="caption" noWrap className="w-40">This is a very long caption that will be truncated if noWrap is true.</Typography>
        <Typography variant="overline" component="div" className="text-gray-500">OVERLINE</Typography>
      </div>
    );
  }

  ---- Tips & extension ideas ----
  - Integrate with a theme context to map `primary` and `secondary` to theme colors.
  - Add responsive variant overrides (e.g., variantSm / variantMd props) if you need fine-grained control.
  - Provide `sx` prop (string of tailwind classes) to allow quick overrides.
*/
